import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TreatmentService, WorkspaceService } from '@pt/api-client';
import {
  DocRenameRequest,
  TreatmentDocCreateRequest,
  TreatmentDocCreateResponse,
  TreatmentDocUpdateRequest,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceMoveRequest,
} from '@primatoo-ms-organization';
import { of } from 'rxjs';
import { PtCoreActions } from '@pt/pt-core';
import { Store } from '@ngrx/store';
import * as TreatmentActions from '../actions/treatment.actions';
import { WidgetDialogService } from '../../services/widget-dialog.service';
import { DialogService } from '../../../services';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';
import { duplicateTreatmentHolderToResponsible } from '../actions/treatment.actions';

@Injectable()
export class TreatmentEffects {
  publishTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.publishTreatment),
      switchMap((action: { docId: number; skipBack?: boolean }) =>
        this.treatmentService.publish(action.docId).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                TreatmentActions.publishTreatmentSuccess(),
                PtCoreActions.showOperationSuccessNotification(),
              ];
            } else {
              return [
                TreatmentActions.publishTreatmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId,
                  },
                }),
                PtCoreActions.showOperationSuccessNotification(),
              ];
            }
          }),
          catchError((err) =>
            of(TreatmentActions.publishTreatmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  publishTreatmentWithConfirm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TreatmentActions.publishTreatmentWithConfirm),
        switchMap(
          (action: {
            docId: number;
            skipBack?: boolean;
            hideInfo?: boolean;
          }) => {
            const title = 'TITLE.TREATMENT_PUBLISH';
            const message = 'MESSAGE.TREATMENT_PUBLISH';
            // const info = 'MESSAGE.ON_END_OPERATION_COME_BACK';

            return this.widgetDialogService
              .runConfirmDialog(
                title,
                message
                // !action.hideInfo ? info : null
              )
              .pipe(
                map((response) => {
                  if (response) {
                    this.store.dispatch(
                      TreatmentActions.publishTreatment({
                        docId: action?.docId,
                        skipBack: action?.skipBack,
                      })
                    );
                  }
                })
              );
          }
        )
      ),
    { dispatch: false }
  );
  validateDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.validateTreatment),
      switchMap(
        (action: {
          docId: number;
          request: ValidateDocumentRequest;
          skipBack?: boolean;
        }) =>
          this.treatmentService.validate(action.docId, action.request).pipe(
            switchMap(() => {
              if (action?.skipBack) {
                return [
                  TreatmentActions.validateTreatmentSuccess(),
                  PtCoreActions.showOperationSuccessNotification(),
                ];
              } else {
                return [
                  TreatmentActions.validateTreatmentSuccess(),
                  PtCoreActions.navigateBack({
                    hop: -1,
                    queryParams: {
                      opened: true,
                      id: action.docId,
                    },
                  }),
                  PtCoreActions.showOperationSuccessNotification(),
                ];
              }
            }),
            catchError((err) =>
              of(
                TreatmentActions.validateTreatmentFailed({ error: err.message })
              )
            )
          )
      )
    );
  });
  validateDocumentWithConfirm$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TreatmentActions.validateTreatmentWithConfirm),
        map(
          (action: {
            docId: number;
            skipBack?: boolean;
            hideInfo?: boolean;
          }) => {
            const title = 'TITLE.TREATMENT_VALIDATE';
            const message = 'MESSAGE.TREATMENT_VALIDATE';
            // const info = 'MESSAGE.ON_END_OPERATION_COME_BACK';
            return this.dialogService
              .runValidateRequestDialog(
                title,
                message
                // !action.hideInfo ? info : null
              )
              .subscribe((request) => {
                if (request) {
                  // this.store.dispatch(PtCoreActions.showOperationRunning());
                  this.store.dispatch(
                    TreatmentActions.validateTreatment({
                      docId: action.docId,
                      request,
                      skipBack: action.skipBack,
                    })
                  );
                } else {
                  this.store.dispatch(
                    TreatmentActions.validateTreatmentFailed({ error: null })
                  );
                }
              });
          }
        )
      );
    },
    { dispatch: false }
  );
  reopenTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.reopenTreatment),
      switchMap((action: { id: number }) =>
        this.treatmentService.reopen(action.id).pipe(
          switchMap(() => [
            TreatmentActions.reopenTreatmentSuccess({ id: action.id }),
            PtCoreActions.showOperationSuccessNotification(),
          ]),
          catchError((err) =>
            of(
              TreatmentActions.reopenTreatmentFailed({
                error: err.message,
              })
            )
          )
        )
      )
    );
  });
  reopenTreatmentWithConfirm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TreatmentActions.reopenTreatmentWithConfirm),
        switchMap((action) => {
          const title = 'TITLE.TREATMENT_REOPEN';
          const message = 'MESSAGE.TREATMENT_REOPEN';

          return this.widgetDialogService.runConfirmDialog(title, message).pipe(
            map((response) => {
              if (response) {
                this.store.dispatch(
                  TreatmentActions.reopenTreatment({
                    id: action?.id,
                  })
                );
              }
            })
          );
        })
      ),
    { dispatch: false }
  );
  updateTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.updateTreatment),
      switchMap((action: { id: number; request: TreatmentDocUpdateRequest }) =>
        this.treatmentService.update(action.id, action.request).pipe(
          map((response: UpdateDocResponse) =>
            TreatmentActions.updateTreatmentSuccess({ response })
          ),
          catchError((err) =>
            of(TreatmentActions.updateTreatmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  renameTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.renameTreatment),
      switchMap((action: { id: number; request: DocRenameRequest }) =>
        this.treatmentService.rename(action.id, action.request).pipe(
          map(() => TreatmentActions.renameTreatmentSuccess({ id: action.id })),
          catchError((err) =>
            of(TreatmentActions.renameTreatmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  createTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.createTreatment),
      switchMap((action: { request: TreatmentDocCreateRequest }) => {
        return this.treatmentService.create(action.request).pipe(
          map((response: TreatmentDocCreateResponse) => {
            return TreatmentActions.createTreatmentSuccess({
              id: response.docId,
              treatmentType: action.request.treatmentType,
            });
          }),
          catchError((err: ErrorEvent) =>
            of(
              TreatmentActions.createTreatmentFailed({
                error: err.error.reason,
              })
            )
          )
          // catchError(err =>
          //   of(TreatmentActions.createTreatmentFailed({ error: err.message }))
          // )
        );
      })
    );
  });

  // moveTreatmentSuccess$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(TreatmentActions.moveTreatmentSuccess),
  //     switchMap((action: { item: WorkspaceItem }) =>
  //
  //       // TODO da ripristinare
  //       // this.treatmentService.getTemplate(action.item.id).pipe(
  //       //   map((response: TemplateWithUser) =>
  //       //     NgrxWidgetActions.updateStoreTemplate({
  //       //       template: response as TreatmentEditable,
  //       //       docType: DocumentEditorType.TREATMENT
  //       //     })
  //       //   ),
  //       //   catchError(() =>
  //       //     of(NgrxWidgetActions.updateStoreTemplate({template: null, docType: null}))
  //       //   )
  //       // )
  //       of()
  //     )
  //   );
  // });
  moveTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.moveTreatment),
      switchMap(
        (action: {
          id: number;
          workspaceMoveRequest: WorkspaceMoveRequest;
          workspaceItemType: WorkspaceItemType;
        }) =>
          this.workspaceService
            .move(
              action.id,
              action.workspaceItemType,
              action.workspaceMoveRequest
            )
            .pipe(
              map((item: WorkspaceItem) =>
                TreatmentActions.moveTreatmentSuccess({ item })
              ),
              catchError((err) =>
                of(TreatmentActions.moveTreatmentFailed({ error: err.message }))
              )
            )
      )
    );
  });

  duplicateTreatmentHolderToResponsible$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TreatmentActions.duplicateTreatmentHolderToResponsible),
      switchMap((action) => {
        return this.treatmentService.duplicate(action.id, action.request).pipe(
          map((response) =>
            TreatmentActions.duplicateTreatmentSuccess({
              response,
            })
          ),
          catchError((err) =>
            of(
              TreatmentActions.duplicateTreatmentFailed({
                error: err.message,
              })
            )
          )
        );
      })
    )
  );
  duplicateTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TreatmentActions.duplicateTreatment),
      switchMap((action) => {
        return this.treatmentService.duplicate(action.id, action.request).pipe(
          map((response) =>
            TreatmentActions.duplicateTreatmentSuccess({
              response,
            })
          ),
          catchError((err) =>
            of(
              TreatmentActions.duplicateTreatmentFailed({
                error: err.message,
              })
            )
          )
        );
      })
    )
  );
  deleteTreatment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TreatmentActions.deleteTreatment),
      switchMap((action) =>
        this.treatmentService.delete(action.id).pipe(
          map(() => TreatmentActions.deleteTreatmentSuccess()),
          catchError((err) =>
            of(TreatmentActions.deleteTreatmentFailed({ error: err.message }))
          )
        )
      )
    );
  });

  constructor(
    private store: Store,
    private actions$: Actions,
    private treatmentService: TreatmentService,
    private workspaceService: WorkspaceService,
    private widgetDialogService: WidgetDialogService,
    private dialogService: DialogService
  ) {}
}
